import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { Reveal, Overline } from "@/components/site/Reveal";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function Join() {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setStatus("loading");
    try {
      await axios.post(`${API}/clan/join`, { ...form, language: lang });
      setStatus("success");
      toast.success(t.join.success);
      setForm({ name: "", email: "", message: "" });
      window.dispatchEvent(new CustomEvent("clan-joined"));
    } catch (err) {
      setStatus("idle");
      toast.error(t.join.error);
    }
  };

  const field =
    "w-full rounded-md border border-clan-ink/10 bg-clan-ink/[0.04] px-4 py-3.5 font-sans text-clan-ink placeholder:text-clan-muted/70 transition-colors duration-300 focus:border-clan-accent focus:bg-clan-ink/[0.06] focus:outline-none focus:ring-1 focus:ring-clan-accent";

  return (
    <section id="join" data-testid="join-section" className="relative overflow-hidden bg-clan-surface py-28 lg:py-40">
      <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-2 lg:gap-24 lg:px-10">
        <div>
          <Reveal>
            <Overline>{t.join.overline}</Overline>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl font-light leading-[1.05] tracking-tight text-clan-ink sm:text-5xl lg:text-6xl pearl">
              {t.join.title}
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-md text-base leading-relaxed text-clan-muted">{t.join.body}</p>
          </Reveal>
          <Reveal delay={0.3}>
            <p className="mt-10 font-serif text-2xl italic text-clan-accent/90">Per mare per terras</p>
          </Reveal>
        </div>

        <Reveal delay={0.15} y={40}>
          <form onSubmit={submit} data-testid="join-form" className="flex flex-col gap-4 rounded-lg border border-clan-ink/10 bg-clan-bg/50 p-7 backdrop-blur-sm lg:p-9">
            <input
              data-testid="join-name-input"
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder={t.join.name}
              className={field}
            />
            <input
              data-testid="join-email-input"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder={t.join.email}
              className={field}
            />
            <textarea
              data-testid="join-message-input"
              rows={4}
              value={form.message}
              onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
              placeholder={t.join.message}
              className={`${field} resize-none`}
            />
            <button
              data-testid="join-submit-btn"
              type="submit"
              disabled={status === "loading"}
              className="mt-2 flex items-center justify-center gap-2 rounded-md bg-clan-primary px-7 py-3.5 font-mono text-xs uppercase tracking-widest text-white transition-colors duration-300 hover:bg-clan-primaryHover focus:outline-none focus:ring-2 focus:ring-clan-accent disabled:opacity-60"
            >
              {status === "loading" ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> {t.join.submitting}
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" /> {t.join.submit}
                </>
              )}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
